/**
 * InputManager — a fachada de entrada que a gameplay consome (implements GameInput).
 * Combina o TouchInput (interface OFICIAL, mobile) com o teclado/mouse (SOMENTE DEV/DEBUG).
 * A gameplay não sabe qual origem está ativa: recebe apenas ações abstratas.
 * Auto-aim: no toque, aimNdc = null (a gameplay mira no alvo mais próximo).
 */
import { Vector2 } from "three";
import type { GameInput } from "./GameInput";
import { Input } from "./Input";
import { TouchInput } from "./TouchInput";

export class InputManager implements GameInput {
  readonly touch: TouchInput;
  private readonly kb: Input; // DEV: teclado + mouse
  private readonly _move = { x: 0, y: 0 };

  constructor(target: HTMLElement, uiRoot: HTMLElement) {
    this.kb = new Input(target);
    this.touch = new TouchInput(uiRoot);
  }

  get move(): Readonly<{ x: number; y: number }> {
    const t = this.touch.move;
    if (t.x !== 0 || t.y !== 0) return t;
    return this.kb.moveVector(this._move); // fallback WASD (dev)
  }

  hookPressed(): boolean {
    return this.touch.hookPressed() || this.kb.mouseRightPressed();
  }
  sprintPressed(): boolean {
    return this.touch.sprintPressed() || this.kb.pressed("Space"); // Space = DEV
  }
  spellPressed(): boolean {
    return this.touch.spellPressed() || this.kb.pressed("KeyQ"); // 10.9U — Q = DEV
  }
  spell2Pressed(): boolean { return this.touch.spell2Pressed() || this.kb.pressed("KeyE"); } // 10.9CX — E = DEV
  spell3Pressed(): boolean { return this.touch.spell3Pressed() || this.kb.pressed("KeyR"); } // 10.9CX — R = DEV
  interactPressed(): boolean {
    return this.touch.interactPressed() || this.kb.pressed("KeyE");
  }
  pausePressed(): boolean {
    return this.touch.pausePressed() || this.kb.pressed("Escape");
  }
  debugTogglePressed(): boolean {
    return this.kb.pressed("F3"); // DEV
  }

  /** Sem mira apontável no toque => auto-aim (null). Mouse (dev) fornece NDC. */
  get aimNdc(): Vector2 | null {
    return this.touch.usingTouch ? null : this.kb.mouseNdc;
  }

  setEnabled(enabled: boolean): void {
    this.kb.setEnabled(enabled);
    this.touch.setEnabled(enabled);
  }

  update(): void {
    this.kb.update();
    this.touch.update();
  }
}
